import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Container, Row, Col, Modal } from "react-bootstrap";
import { FaCalendarAlt, FaStar, FaTimes, FaEnvelope, FaLink, FaShareAlt } from "react-icons/fa";
import { getNews, urlFor } from "../lib/sanity";
import newsDataFallback from "../data/news.json";
import styles from "./News.module.css";

const News = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [newsData, setNewsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [usingSanity, setUsingSanity] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    const fetchNews = async () => {
      setLoading(true);

      // Try to fetch from Sanity first
      const sanityNews = await getNews();

      if (sanityNews && sanityNews.length > 0) {
        setNewsData(sanityNews);
        setUsingSanity(true);
      } else {
        // Fallback to local JSON
        setNewsData(newsDataFallback);
        setUsingSanity(false);
      }

      setLoading(false);
    };

    fetchNews();
  }, []);

  // Handle eventId from URL - auto-open modal if event exists
  useEffect(() => {
    if (eventId && newsData.length > 0) {
      const event = newsData.find(e => (e._id || e.id) === eventId);
      if (event) {
        setSelectedEvent(event);
        setShowModal(true);
      }
    }
  }, [eventId, newsData]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const getImageUrl = (event, fullSize = false) => {
    // If using Sanity and event has a Sanity image object
    if (usingSanity && event.image && event.image.asset) {
      if (fullSize) {
        return urlFor(event.image).width(1200).url();
      }
      return urlFor(event.image).width(600).height(400).url();
    }
    // Otherwise use the direct URL (from JSON fallback)
    return event.image;
  };

  const getMetaData = () => {
    const baseUrl = window.location.origin;

    if (selectedEvent) {
      const eventUrl = `${baseUrl}/news/${selectedEvent._id || selectedEvent.id}`;
      const imageUrl = getImageUrl(selectedEvent, true);

      return {
        title: `${selectedEvent.title} - Hood Shaw Memorial`,
        description: selectedEvent.description,
        url: eventUrl,
        image: imageUrl,
        type: 'article'
      };
    }

    return {
      title: 'News & Events - Hood Shaw Memorial',
      description: 'Stay updated with the latest happenings at Hood Shaw Memorial',
      url: `${baseUrl}/news`,
      image: `${baseUrl}/hs.jpg`,
      type: 'website'
    };
  };

  const metaData = getMetaData();

  const handleEventClick = (event) => {
    const eventId = event._id || event.id;
    navigate(`/news/${eventId}`);
    setSelectedEvent(event);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    navigate('/news');
    setShowModal(false);
    setSelectedEvent(null);
  };

  const handleShare = async (platform, event) => {
    const eventId = event._id || event.id;
    const baseUrl = window.location.origin;
    const url = `${baseUrl}/news/${eventId}`;
    const title = event.title;
    const description = event.description;

    switch (platform) {
      case 'native':
        // Try native share if available (mobile)
        if (navigator.share) {
          try {
            await navigator.share({
              title: title,
              text: description,
              url: url
            });
          } catch (err) {
            // User cancelled, do nothing
          }
        } else {
          // Fallback to copy
          navigator.clipboard.writeText(url).then(() => {
            alert('Link copied to clipboard!');
          });
        }
        break;
      case 'email':
        window.location.href = `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(description + '\n\n' + url)}`;
        break;
      case 'copy':
        navigator.clipboard.writeText(url).then(() => {
          alert('Link copied to clipboard!');
        });
        break;
      default:
        break;
    }
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <Container>
          <div className={styles.header}>
            <h1 className={styles.title}>Loading...</h1>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <Helmet>
        <title>{metaData.title}</title>
        <meta name="description" content={metaData.description} />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content={metaData.type} />
        <meta property="og:url" content={metaData.url} />
        <meta property="og:title" content={metaData.title} />
        <meta property="og:description" content={metaData.description} />
        <meta property="og:image" content={metaData.image} />

        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={metaData.url} />
        <meta property="twitter:title" content={metaData.title} />
        <meta property="twitter:description" content={metaData.description} />
        <meta property="twitter:image" content={metaData.image} />
      </Helmet>

      <Container>
        <div className={styles.header} data-aos="fade-up">
          <h1 className={styles.title}>News & Events</h1>
          <p className={styles.subtitle}>
            Stay updated with the latest happenings at Hood Shaw Memorial
          </p>
        </div>

        <Row>
          {newsData.length > 0 ? (
            newsData.map((event, index) => (
              <Col
                key={event._id || event.id}
                md={6}
                lg={4}
                className="mb-4"
                data-aos="fade-up"
                data-aos-delay={index * 100}
              >
                <div
                  className={styles.eventCard}
                  onClick={() => handleEventClick(event)}
                  style={{ cursor: "pointer" }}
                >
                  {event.featured && (
                    <div className={styles.featuredBadge}>
                      <FaStar /> Featured
                    </div>
                  )}
                  <div className={styles.imageContainer}>
                    <img
                      src={getImageUrl(event)}
                      alt={event.title}
                      className={styles.eventImage}
                    />
                  </div>
                  <div className={styles.cardBody}>
                    <span className={styles.categoryBadge}>{event.category}</span>
                    <h3 className={styles.eventTitle}>{event.title}</h3>
                    <p className={styles.eventDate}>
                      <FaCalendarAlt /> {formatDate(event.date)}
                    </p>
                    <p className={styles.eventDescription}>{event.description}</p>
                  </div>
                </div>
              </Col>
            ))
          ) : (
            <Col>
              <div className={styles.emptyState}>
                <h3>No events found</h3>
                <p>Check back soon for upcoming events and news.</p>
              </div>
            </Col>
          )}
        </Row>

        <Modal
          show={showModal}
          onHide={handleCloseModal}
          size="lg"
          centered
          contentClassName={styles.modalContent}
        >
          <Modal.Body className={styles.modalBody}>
            {selectedEvent && (
              <>
                <button
                  className={styles.closeButton}
                  onClick={handleCloseModal}
                  aria-label="Close"
                >
                  <FaTimes />
                </button>
                {selectedEvent.featured && (
                  <div className={styles.modalFeaturedBadge}>
                    <FaStar /> Featured
                  </div>
                )}
                <div className={styles.modalImageContainer}>
                  <img
                    src={getImageUrl(selectedEvent, true)}
                    alt={selectedEvent.title}
                    className={styles.modalImage}
                  />
                </div>
                <div className={styles.modalContentBody}>
                  <span className={styles.categoryBadge}>
                    {selectedEvent.category}
                  </span>
                  <h2 className={styles.modalTitle}>{selectedEvent.title}</h2>
                  <p className={styles.modalDate}>
                    <FaCalendarAlt /> {formatDate(selectedEvent.date)}
                  </p>
                  <p className={styles.modalDescription}>
                    {selectedEvent.description}
                  </p>
                  <div className={styles.shareSection}>
                    <h4 className={styles.shareTitle}>Share this event:</h4>
                    <div className={styles.shareButtons}>
                      <button
                        className={styles.shareButton}
                        onClick={() => handleShare('native', selectedEvent)}
                        aria-label="Share"
                        title="Share via apps"
                      >
                        <FaShareAlt />
                      </button>
                      <button
                        className={styles.shareButton}
                        onClick={() => handleShare('email', selectedEvent)}
                        aria-label="Share via Email"
                        title="Share via Email"
                      >
                        <FaEnvelope />
                      </button>
                      <button
                        className={styles.shareButton}
                        onClick={() => handleShare('copy', selectedEvent)}
                        aria-label="Copy Link"
                        title="Copy link to clipboard"
                      >
                        <FaLink />
                      </button>
                    </div>
                  </div>
                </div>
              </>
            )}
          </Modal.Body>
        </Modal>
      </Container>
    </div>
  );
};

export default News;
